import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import {
  ContactType,
  Address,
  formatAddress,
} from "@akfdev-com/shared/src/models";
import { AddressForm, PartialAddress } from "../../components/ui";
import { contactsApi } from "../../services/api";

interface ContactFormData {
  name: string;
  type: ContactType;
  email: string;
  phone: string;
  notes: string;
}

const ContactAdd: React.FC = () => {
  const navigate = useNavigate();
  const [saveError, setSaveError] = useState<string | null>(null);
  // Structured Address state (address-geolocation Rule 21). Managed alongside
  // useForm because <AddressForm> exposes a controlled value/onChange pair
  // rather than a name-registerable single input.
  const [structuredAddress, setStructuredAddress] = useState<PartialAddress>(
    {},
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      name: "",
      type: "supplier",
      email: "",
      phone: "",
      notes: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      setSaveError(null);
      // Derive legacy `address` string from structured fields so back-compat
      // readers continue to work (address-geolocation Req 11.1, 11.2). Only
      // send `structuredAddress` when the user has entered at least one
      // required field — an empty AddressForm produces no address at all.
      const hasAnyStructured =
        !!structuredAddress.addressLine1 ||
        !!structuredAddress.town ||
        !!structuredAddress.postcode ||
        !!structuredAddress.countryCode;
      const asAddress: Address = {
        organisation: structuredAddress.organisation,
        addressLine1: structuredAddress.addressLine1 ?? "",
        addressLine2: structuredAddress.addressLine2,
        locality: structuredAddress.locality,
        town: structuredAddress.town ?? "",
        county: structuredAddress.county,
        postcode: structuredAddress.postcode ?? "",
        countryCode: structuredAddress.countryCode ?? "",
      };
      const legacyAddress = hasAnyStructured ? formatAddress(asAddress) : "";
      const payload: any = {
        ...data,
        address: legacyAddress,
        ...(hasAnyStructured ? { structuredAddress: asAddress } : {}),
      };
      const created = await contactsApi.create(payload);
      navigate(`/dashboard/contacts/${created.id}`);
    } catch {
      setSaveError("Failed to add contact. Please try again.");
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
        <Link
          to="/dashboard/contacts"
          className="hover:text-primary-600 transition-colors"
        >
          Contacts
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Add Contact</span>
      </div>

      <PageHeader
        title="Add Contact"
        description="Create a new resident, supplier, or customer contact."
      />

      {saveError && (
        <div className="mt-4">
          <ErrorAlert message={saveError} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Contact Information
            </h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="form-label">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                className={`form-input mt-1 ${
                  errors.name
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="Full name or company name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label htmlFor="type" className="form-label">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                id="type"
                className="form-input mt-1"
                {...register("type", { required: true })}
              >
                <option value="resident">Resident</option>
                <option value="supplier">Supplier</option>
                <option value="customer">Customer</option>
              </select>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                className={`form-input mt-1 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="email@example.com"
                {...register("email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="form-label">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                className="form-input mt-1"
                placeholder="+44 7700 900000"
                {...register("phone")}
              />
            </div>

            {/* Structured Address (address-geolocation Rule 21) */}
            <div>
              <label className="form-label mb-2 block">Address</label>
              <AddressForm
                value={structuredAddress}
                onChange={setStructuredAddress}
                idPrefix="contact-add"
              />
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="form-label">
                Notes
              </label>
              <textarea
                id="notes"
                rows={4}
                className="form-input mt-1 resize-none"
                placeholder="Any additional notes..."
                {...register("notes")}
              />
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between rounded-b-lg">
            <Link
              to="/dashboard/contacts"
              className="inline-flex items-center justify-center w-full sm:w-auto gap-1.5 text-base sm:text-sm text-gray-600 hover:text-gray-900 transition-colors border border-gray-300 rounded-md px-4 py-2.5 sm:py-2 bg-white hover:bg-gray-50 min-h-[44px] sm:min-h-0"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Saving..." : "Add Contact"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactAdd;
